// src/components/organizer/EventManagementWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Add as AddIcon,
  Campaign as PromoteIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

const EventManagementWidget = ({ data }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  if (!data || !Array.isArray(data)) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader title="🎯 Gestión de Eventos" />
        <CardContent>
          <Alert severity="info">
            No tienes eventos creados aún. ¡Crea tu primer evento!
          </Alert>
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ mt: 2 }}
            startIcon={<AddIcon />}
          >
            Crear Primer Evento
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleMenuOpen = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'default',
      'published': 'primary',
      'registration_open': 'success',
      'registration_closed': 'warning',
      'in_progress': 'info',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Borrador',
      'published': 'Publicado',
      'registration_open': 'Inscripciones Abiertas',
      'registration_closed': 'Inscripciones Cerradas',
      'in_progress': 'En Progreso',
      'completed': 'Completado',
      'cancelled': 'Cancelado'
    };
    return labels[status] || status;
  };

  const getCapacityPercentage = (registered, capacity) => {
    return Math.round((registered / capacity) * 100);
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="🎯 Mis Eventos"
        subheader={`${data.length} eventos en gestión`}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            Nuevo Evento
          </Button>
        }
      />
      
      <CardContent>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Participantes</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {event.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.sport} • {event.category}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={getStatusLabel(event.status)}
                      color={getStatusColor(event.status)}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {event.registered}/{event.capacity}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getCapacityPercentage(event.registered, event.capacity)}
                        size="small"
                        sx={{ height: 4, borderRadius: 2 }}
                        color={getCapacityPercentage(event.registered, event.capacity) > 80 ? 'warning' : 'primary'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {getCapacityPercentage(event.registered, event.capacity)}% ocupado
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(event.date).toLocaleDateString('es-ES')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.time}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, event)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => console.log('Ver evento', selectedEvent)}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => console.log('Editar evento', selectedEvent)}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Editar Evento
          </MenuItem>
          <MenuItem onClick={() => console.log('Gestionar participantes', selectedEvent)}>
            <PeopleIcon sx={{ mr: 1 }} fontSize="small" />
            Participantes
          </MenuItem>
          <MenuItem onClick={() => console.log('Promocionar evento', selectedEvent)}>
            <PromoteIcon sx={{ mr: 1 }} fontSize="small" />
            Promocionar
          </MenuItem>
          <MenuItem onClick={() => console.log('Ver reporte', selectedEvent)}>
            <ReportIcon sx={{ mr: 1 }} fontSize="small" />
            Reporte
          </MenuItem>
        </Menu>

        {/* Estadísticas rápidas */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            📈 Resumen: {data.filter(e => e.status === 'registration_open').length} con inscripciones abiertas, {' '}
            {data.reduce((sum, e) => sum + e.registered, 0)} participantes totales
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EventManagementWidget;